import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormData,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { TextTemplateItem } from '@tellent-ask-hr/templates-types';

export class TextMultiLineConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind: TextTemplateItem['type'] = 'text';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.text_multi_line'),
    icon: 'format-align-left',
  };

  public readonly components = {
    define: () => import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondTextComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').QuestionComponent['defineForm']
  > => this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondTextComponent['respondConfig']
  > => ({
    ...data,
    kind: 'multiline',
    placeholder: this.i18n.translate('tlc.ask_hr.form_template_create.configs.respond_placeholder'),
  });

  public readonly respondForm = (
    config: RespondFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondTextComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>('', {
        validators: config.required ? Validators.required : [],
      }),
    });

  public readonly presentConfig = (
    data: TextTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'text',
      multiline: true,
    },
  });
}
